import { useEffect, useState } from "react";
import { T } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn } from "../components";

const StatusDot = ({ on }) => (
  <span style={{
    width: 8, height: 8, borderRadius: "50%", flexShrink: 0, display: "inline-block",
    background: on ? "#22c55e" : T.text3,
    boxShadow: on ? "0 0 0 3px rgba(34,197,94,0.2)" : "none",
    animation: on ? "pulse 2s infinite" : "none",
  }} />
);

const ORDER_STATUS_COLORS = {
  processing: { bg: "rgba(99,102,241,0.1)",  color: "#6366f1",  label: "Processing"  },
  pending:    { bg: "rgba(245,158,11,0.1)",   color: "#f59e0b",  label: "Pending"     },
  "on-hold":  { bg: "rgba(245,158,11,0.1)",   color: "#f59e0b",  label: "On Hold"     },
  completed:  { bg: "rgba(34,197,94,0.1)",    color: "#22c55e",  label: "Completed"   },
  cancelled:  { bg: "rgba(244,63,94,0.1)",    color: "#f43f5e",  label: "Cancelled"   },
  refunded:   { bg: "rgba(244,63,94,0.1)",    color: "#f43f5e",  label: "Refunded"    },
  failed:     { bg: "rgba(244,63,94,0.1)",    color: "#f43f5e",  label: "Failed"      },
  shipped:    { bg: "rgba(34,197,94,0.1)",    color: "#22c55e",  label: "Shipped"     },
};
const statusStyle = (s) => ORDER_STATUS_COLORS[s] || { bg: T.bg3, color: T.text3, label: s };

export default function WooCommercePage({ token, plan, setPage }) {
  const [tab,          setTab]          = useState("connect");
  const [status,       setStatus]       = useState(null);
  const [products,     setProducts]     = useState([]);
  const [orders,       setOrders]       = useState([]);
  const [ordersLoad,   setOrdersLoad]   = useState(false);
  const [siteUrl,      setSiteUrl]      = useState("");
  const [ck,           setCk]           = useState("");
  const [cs,           setCs]           = useState("");
  const [showCs,       setShowCs]       = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [hookLoading,  setHookLoading]  = useState(false);
  const [error,        setError]        = useState("");
  const [success,      setSuccess]      = useState("");
  const [hookMsg,      setHookMsg]      = useState("");
  const [hookErr,      setHookErr]      = useState("");
  const [prodLoad,     setProdLoad]     = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [countryCode,  setCountryCode]  = useState("92");
  const [orderMsgOn,   setOrderMsgOn]   = useState(true);
  const [serverUrl,    setServerUrl]    = useState("");
  const [urlSaved,     setUrlSaved]     = useState(false);
  const [testPhone,    setTestPhone]    = useState("");
  const [testLoading,  setTestLoading]  = useState(false);
  const [testMsg,      setTestMsg]      = useState("");
  const [testErr,      setTestErr]      = useState("");

  const isPro = plan && plan !== "free";

  useEffect(() => {
    if (!isPro) { setLoading(false); return; }
    apiFetch("/api/woocommerce/status", {}, token)
      .then(d => {
        setStatus(d);
        setCountryCode(d.countryCode || "92");
        setOrderMsgOn(d.orderMsgEnabled !== false);
        setServerUrl(d.serverUrl || "");
        if (d.connected) setTab("overview");
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token, isPro]);

  const loadProducts = () => {
    setProdLoad(true);
    apiFetch("/api/woocommerce/products", {}, token)
      .then(d => setProducts(d.products || []))
      .catch(() => {})
      .finally(() => setProdLoad(false));
  };

  const loadOrders = () => {
    setOrdersLoad(true);
    apiFetch("/api/woocommerce/recent-orders", {}, token)
      .then(d => setOrders(d.orders || []))
      .catch(() => {})
      .finally(() => setOrdersLoad(false));
  };

  useEffect(() => {
    if (!status?.connected) return;
    loadProducts();
  }, [status?.connected]);

  useEffect(() => {
    if (tab === "orders" && status?.connected) loadOrders();
  }, [tab]);

  const connect = async () => {
    if (!siteUrl.trim() || !ck.trim() || !cs.trim()) return setError("All three fields are required");
    setSaving(true); setError(""); setSuccess("");
    try {
      const d = await apiFetch("/api/woocommerce/connect", {
        method: "POST",
        body: JSON.stringify({ siteUrl: siteUrl.trim(), consumerKey: ck.trim(), consumerSecret: cs.trim() }),
      }, token);
      if (d.error) { setError(d.error); }
      else {
        setSuccess(`Connected to ${d.shopName}!${d.webhookRegistered ? " Webhook registered automatically ✓" : " (Webhook registration needed — see Automation tab)"}`);
        setStatus(s => ({
          ...s, connected: true, enabled: true,
          siteUrl: d.siteUrl, shopName: d.shopName, currency: d.currency,
          webhookRegistered: d.webhookRegistered, webhookUrl: d.webhookUrl,
        }));
        setSiteUrl(""); setCk(""); setCs("");
        setTab("overview");
      }
    } catch { setError("Network error — please try again"); }
    setSaving(false);
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect WooCommerce? Bot will stop sending order messages.")) return;
    await apiFetch("/api/woocommerce/disconnect", { method: "DELETE" }, token);
    setStatus({ connected: false, enabled: false, webhookRegistered: false });
    setProducts([]); setOrders([]);
    setTab("connect");
  };

  const toggleEnabled = async (val) => {
    await apiFetch("/api/woocommerce/toggle", { method: "PATCH", body: JSON.stringify({ enabled: val }) }, token);
    setStatus(s => ({ ...s, enabled: val }));
  };

  const saveSettings = async (cc, msgOn, sUrl) => {
    await apiFetch("/api/woocommerce/settings", {
      method: "PATCH", body: JSON.stringify({ countryCode: cc, orderMsgEnabled: msgOn, serverUrl: sUrl ?? serverUrl }),
    }, token).catch(() => {});
  };

  const saveServerUrl = async () => {
    await saveSettings(countryCode, orderMsgOn, serverUrl);
    setUrlSaved(true);
    setStatus(s => ({ ...s, webhookRegistered: false, webhookUrl: serverUrl ? `${serverUrl}/api/woocommerce/webhook/${s.webhookUrl?.split("/api/")[1]?.split("/")[2] || ""}` : s.webhookUrl }));
    setTimeout(() => setUrlSaved(false), 2000);
  };

  const setupWebhook = async () => {
    setHookLoading(true); setHookErr(""); setHookMsg("");
    try {
      const d = await apiFetch("/api/woocommerce/setup-webhook", { method: "POST" }, token);
      if (d.error) setHookErr(d.error);
      else {
        setHookMsg(`Webhook registered! (${(d.registered || []).join(", ")})`);
        setStatus(s => ({ ...s, webhookRegistered: true, webhookUrl: d.webhookUrl }));
      }
    } catch { setHookErr("Network error — try again"); }
    setHookLoading(false);
  };

  const sendTest = async () => {
    if (!testPhone.trim()) return setTestErr("Enter your WhatsApp number first");
    setTestLoading(true); setTestErr(""); setTestMsg("");
    try {
      const d = await apiFetch("/api/woocommerce/test-message", {
        method: "POST", body: JSON.stringify({ testPhone: testPhone.trim() }),
      }, token);
      if (d.error) setTestErr(d.error);
      else setTestMsg("Test message sent! Check WhatsApp on " + testPhone);
    } catch { setTestErr("Failed to send"); }
    setTestLoading(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(status?.webhookUrl || "").then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Pro gate ────────────────────────────────────────────────────
  if (!isPro) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ maxWidth: 440, textAlign: "center" }}>
        <div style={{ width: 72, height: 72, borderRadius: 20, margin: "0 auto 24px", background: "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.10))", border: "1px solid rgba(99,102,241,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="1.8"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.text1, marginBottom: 10 }}>WooCommerce is a Pro Feature</div>
        <div style={{ fontSize: 14, color: T.text3, lineHeight: 1.6, marginBottom: 24 }}>Auto order confirmations, status updates & pipeline sync.</div>
        <button onClick={() => setPage?.("plan")} style={{ width: "100%", padding: "13px 24px", borderRadius: 12, border: "none", cursor: "pointer", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", color: "#fff", fontSize: 14, fontWeight: 700 }}>
          Upgrade Plan
        </button>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 28, height: 28, border: `3px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </div>
  );

  const TABS = status?.connected
    ? [
        { id: "overview",   label: "Overview"   },
        { id: "orders",     label: "Orders"      },
        { id: "products",   label: "Products"    },
        { id: "automation", label: "Automation"  },
        { id: "connect",    label: "Settings"    },
      ]
    : [{ id: "connect", label: "Connect" }];

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Header */}
      <div style={{ padding: "20px 28px 0", background: T.bg1, borderBottom: `1px solid ${T.border1}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: "linear-gradient(135deg, #7f54b3, #a65cbe)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(127,84,179,0.35)" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M3 9l3 9 3-5 3 5 3-9" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: T.text1 }}>WooCommerce</div>
            <div style={{ fontSize: 12, color: T.text3 }}>
              {status?.connected ? `${status.shopName} · ${status.currency}` : "Not connected"}
            </div>
          </div>
          {status?.connected && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              <StatusDot on={status.enabled} />
              <span style={{ fontSize: 12, color: T.text3 }}>{status.enabled ? "Active" : "Paused"}</span>
              <div onClick={() => toggleEnabled(!status.enabled)} style={{ width: 36, height: 20, borderRadius: 10, cursor: "pointer", position: "relative", background: status.enabled ? T.indigo : T.border2, transition: "background 0.2s" }}>
                <div style={{ position: "absolute", top: 2, left: status.enabled ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
              </div>
              <button onClick={disconnect} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${T.rose}44`, background: "transparent", color: T.rose, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Disconnect</button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4 }}>
          {TABS.map(t => (
            <div key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "8px 16px", fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? T.indigo : T.text3, cursor: "pointer",
              borderBottom: tab === t.id ? `2px solid ${T.indigo}` : "2px solid transparent",
              transition: "all 0.15s",
            }}>{t.label}</div>
          ))}
        </div>
      </div>

      {/* ── Webhook warning banner ───────────────────────────────── */}
      {status?.connected && !status?.webhookRegistered && (
        <div style={{ padding: "10px 28px", background: "rgba(245,158,11,0.08)", borderBottom: "1px solid rgba(245,158,11,0.25)", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <span style={{ fontSize: 12, color: "#f59e0b", flex: 1 }}>
            <strong>Webhook not registered</strong> — Order messages won't be sent until you register the webhook.
          </span>
          <button onClick={() => setTab("automation")} style={{ padding: "4px 12px", borderRadius: 7, border: "1px solid rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.12)", color: "#f59e0b", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            Fix Now →
          </button>
        </div>
      )}

      {/* ── Tab content ──────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto" }}>

        {/* OVERVIEW TAB */}
        {tab === "overview" && status?.connected && (
          <div style={{ padding: "24px 28px" }}>
            {/* Stats row */}
            <div className="grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
              {[
                { label: "Store",      value: status.shopName,                          color: T.text1 },
                { label: "Currency",   value: status.currency,                          color: T.indigo },
                { label: "Webhook",    value: status.webhookRegistered ? "Active" : "Not Registered", color: status.webhookRegistered ? "#22c55e" : "#f59e0b" },
                { label: "Bot",        value: status.enabled ? "Sending Messages" : "Paused",         color: status.enabled ? "#22c55e" : T.text3 },
              ].map(s => (
                <div key={s.label} style={{ padding: "14px 16px", borderRadius: 12, background: T.bg1, border: `1px solid ${T.border1}` }}>
                  <div style={{ fontSize: 10, color: T.text3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.value || "—"}</div>
                </div>
              ))}
            </div>

            {/* Recent orders preview */}
            <div style={{ padding: "20px 22px", borderRadius: 14, background: T.bg1, border: `1px solid ${T.border1}`, marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>Recent Orders</div>
                <button onClick={() => setTab("orders")} style={{ fontSize: 12, color: T.indigo, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>View all →</button>
              </div>
              {orders.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: T.text3, fontSize: 13 }}>
                  <div style={{ marginBottom: 8 }}>No orders loaded yet</div>
                  <button onClick={loadOrders} style={{ fontSize: 12, color: T.indigo, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Load orders</button>
                </div>
              ) : (
                orders.slice(0, 5).map(o => {
                  const s = statusStyle(o.status);
                  return (
                    <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.border1}` }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: T.bg2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 13, fontWeight: 700, color: T.text3 }}>#{o.id}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>{o.billing?.first_name} {o.billing?.last_name}</div>
                        <div style={{ fontSize: 11, color: T.text3 }}>{o.billing?.phone} · {o.line_items?.length || 0} item(s)</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>{o.currency} {o.total}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: s.bg, color: s.color }}>{s.label}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* How it works */}
            <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {[
                { icon: "🛒", title: "Order Placed",       desc: "WooCommerce fires a webhook → bot receives it instantly" },
                { icon: "💬", title: "WhatsApp Sent",      desc: "Bot auto-sends order confirmation to customer's number" },
                { icon: "🚀", title: "Pipeline Updated",   desc: "Customer added as My Client with order value" },
                { icon: "📦", title: "Status Updates",     desc: "Shipped / Delivered / Cancelled messages sent automatically" },
                { icon: "❓", title: "Order Queries",      desc: "Customer asks about order → bot replies with live status" },
                { icon: "🤝", title: "Human Handoff",      desc: "Complex issues escalated to your team as usual" },
              ].map(c => (
                <div key={c.title} style={{ padding: "14px 16px", borderRadius: 12, background: T.bg1, border: `1px solid ${T.border1}` }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{c.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text1, marginBottom: 4 }}>{c.title}</div>
                  <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.6 }}>{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ORDERS TAB */}
        {tab === "orders" && (
          <div style={{ padding: "24px 28px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text1 }}>Recent Orders</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Last 20 orders from your WooCommerce store</div>
              </div>
              <Btn variant="secondary" onClick={loadOrders} disabled={ordersLoad}>
                {ordersLoad ? "Loading…" : "Refresh"}
              </Btn>
            </div>

            {ordersLoad ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                <div style={{ width: 28, height: 28, border: `3px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              </div>
            ) : orders.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: T.text3, fontSize: 14 }}>No orders found</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {orders.map(o => {
                  const s = statusStyle(o.status);
                  const date = o.date_created ? new Date(o.date_created).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" }) : "";
                  return (
                    <div key={o.id} style={{ padding: "16px 18px", borderRadius: 12, background: T.bg1, border: `1px solid ${T.border1}`, display: "flex", alignItems: "flex-start", gap: 14 }}>
                      {/* Order number */}
                      <div style={{ width: 44, height: 44, borderRadius: 10, background: T.bg2, border: `1px solid ${T.border1}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: T.text3 }}>#{o.id}</span>
                      </div>

                      {/* Customer */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>{o.billing?.first_name} {o.billing?.last_name}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: s.bg, color: s.color, border: `1px solid ${s.color}33` }}>{s.label}</span>
                        </div>
                        <div style={{ fontSize: 12, color: T.text3, marginBottom: 6 }}>
                          📱 {o.billing?.phone || "—"} &nbsp;·&nbsp; 📧 {o.billing?.email || "—"}
                        </div>
                        {/* Items */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {(o.line_items || []).map((item, i) => (
                            <span key={i} style={{ fontSize: 11, padding: "3px 9px", borderRadius: 8, background: T.bg2, border: `1px solid ${T.border1}`, color: T.text2 }}>
                              {item.name} ×{item.quantity}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Amount & date */}
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: T.text1 }}>{o.currency} {o.total}</div>
                        <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>{date}</div>
                        <div style={{ fontSize: 11, color: o.billing?.phone ? T.indigo : T.text3, marginTop: 2 }}>
                          {o.billing?.phone ? "✓ Has phone" : "✗ No phone"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PRODUCTS TAB */}
        {tab === "products" && (
          <div style={{ padding: "24px 28px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text1 }}>Product Catalog</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Products the bot can answer questions about</div>
              </div>
              <Btn variant="secondary" onClick={loadProducts} disabled={prodLoad}>{prodLoad ? "Loading…" : "Refresh"}</Btn>
            </div>
            {prodLoad ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                <div style={{ width: 28, height: 28, border: `3px solid ${T.border2}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              </div>
            ) : products.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: T.text3, fontSize: 14 }}>No products found</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                {products.map(p => {
                  const inStock = p.stock_status === "instock";
                  const img = p.images?.[0]?.src;
                  return (
                    <div key={p.id} style={{ padding: "14px 16px", borderRadius: 12, background: T.bg1, border: `1px solid ${T.border1}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
                      {img ? (
                        <img src={img} alt={p.name} style={{ width: 48, height: 48, borderRadius: 9, objectFit: "cover", flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 48, height: 48, borderRadius: 9, background: T.bg2, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                        <div style={{ fontSize: 12, color: T.text2 }}>
                          {p.price ? `${status.currency} ${p.price}` : "—"}
                          {p.sale_price && <span style={{ color: T.rose, marginLeft: 6 }}>Sale: {p.sale_price}</span>}
                        </div>
                        <span style={{ display: "inline-block", marginTop: 6, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: inStock ? "rgba(34,197,94,0.1)" : "rgba(244,63,94,0.1)", color: inStock ? "#22c55e" : "#f43f5e" }}>
                          {inStock ? "In Stock" : "Out of Stock"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* AUTOMATION TAB */}
        {tab === "automation" && (
          <div className="grid-2" style={{ padding: "24px 28px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

            {/* Webhook setup */}
            <div style={{ padding: "22px 24px", borderRadius: 16, background: T.bg1, border: status?.webhookRegistered ? `1px solid ${T.border1}` : "1px solid rgba(245,158,11,0.4)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={status?.webhookRegistered ? "#22c55e" : "#f59e0b"} strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>Order Automation Webhook</div>
                <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: status?.webhookRegistered ? "rgba(34,197,94,0.1)" : "rgba(245,158,11,0.1)", color: status?.webhookRegistered ? "#22c55e" : "#f59e0b", border: `1px solid ${status?.webhookRegistered ? "rgba(34,197,94,0.25)" : "rgba(245,158,11,0.35)"}` }}>
                  {status?.webhookRegistered ? "ACTIVE" : "NOT REGISTERED"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: T.text3, marginBottom: 16, lineHeight: 1.6 }}>
                This URL must be registered in WooCommerce so the bot knows when orders are placed. Click the button below to register automatically.
              </div>

              {/* ⚠️ Server URL — MOST IMPORTANT setting */}
              <div style={{ marginBottom: 16, padding: "14px 16px", borderRadius: 10, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.3)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b", marginBottom: 6 }}>
                  ⚠️ Step 1 — Set your public server URL
                </div>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 10, lineHeight: 1.6 }}>
                  This must be your server's <strong style={{ color: T.text2 }}>public HTTPS URL</strong> (not localhost). WooCommerce will POST to this address. Example: <code style={{ color: T.indigo }}>https://myapp.railway.app</code>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    value={serverUrl}
                    onChange={e => setServerUrl(e.target.value)}
                    placeholder="https://your-server-url.railway.app"
                    style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text1, fontSize: 12, outline: "none", fontFamily: "inherit" }}
                    onFocus={e => (e.target.style.borderColor = "#f59e0b")}
                    onBlur={e  => (e.target.style.borderColor = T.border2)}
                  />
                  <button onClick={saveServerUrl} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: urlSaved ? "rgba(34,197,94,0.2)" : "rgba(245,158,11,0.2)", color: urlSaved ? "#22c55e" : "#f59e0b", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                    {urlSaved ? "✓ Saved" : "Save"}
                  </button>
                </div>
                {status?.detectedUrl && (
                  <div style={{ fontSize: 10, color: T.text3, marginTop: 6 }}>
                    Auto-detected: <code style={{ color: T.text2 }}>{status.detectedUrl}</code>
                    {status.detectedUrl?.includes("localhost") && <span style={{ color: "#f43f5e", marginLeft: 6 }}>← localhost won't work! Set the public URL above.</span>}
                  </div>
                )}
              </div>

              {/* Webhook URL */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: T.text3, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>Webhook URL</label>
                <div style={{ display: "flex", gap: 6 }}>
                  <input readOnly value={status?.webhookUrl || ""} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text2, fontSize: 10, fontFamily: "monospace", outline: "none", overflow: "hidden", textOverflow: "ellipsis" }} />
                  <button onClick={copyUrl} style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.border2}`, background: copied ? "rgba(34,197,94,0.1)" : T.bg2, color: copied ? "#22c55e" : T.text2, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>
                    {copied ? "✓" : "Copy"}
                  </button>
                </div>
              </div>

              {hookErr && <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(244,63,94,0.08)", color: "#f43f5e", fontSize: 12, marginBottom: 10, border: "1px solid rgba(244,63,94,0.2)" }}>{hookErr}</div>}
              {hookMsg && <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(34,197,94,0.08)", color: "#22c55e", fontSize: 12, marginBottom: 10, border: "1px solid rgba(34,197,94,0.2)" }}>{hookMsg}</div>}

              <Btn variant="primary" onClick={setupWebhook} disabled={hookLoading} style={{ width: "100%", marginBottom: 14 }}>
                {hookLoading ? "Registering…" : status?.webhookRegistered ? "Re-register Webhook" : "Register Webhook Now"}
              </Btn>

              <details>
                <summary style={{ fontSize: 12, color: T.indigo, cursor: "pointer", fontWeight: 600 }}>Register manually instead?</summary>
                <div style={{ marginTop: 10, fontSize: 12, color: T.text3, lineHeight: 1.8, borderLeft: `2px solid ${T.border2}`, paddingLeft: 10 }}>
                  <div>1. WooCommerce → Settings → Advanced → <strong style={{ color: T.text2 }}>Webhooks</strong></div>
                  <div>2. Add webhook → Topic: <code style={{ color: T.indigo }}>Order created</code></div>
                  <div>3. Paste the URL above → Save</div>
                  <div>4. Repeat for <code style={{ color: T.indigo }}>Order updated</code></div>
                </div>
              </details>
            </div>

            {/* Right column: settings + test */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Automation settings */}
              <div style={{ padding: "22px 24px", borderRadius: 16, background: T.bg1, border: `1px solid ${T.border1}` }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 16 }}>Settings</div>

                {/* Toggle */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", borderRadius: 10, background: T.bg2, border: `1px solid ${T.border1}`, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text1 }}>Order Confirmation Messages</div>
                    <div style={{ fontSize: 11, color: T.text3, marginTop: 1 }}>Send WhatsApp on order placed</div>
                  </div>
                  <div onClick={() => { const v = !orderMsgOn; setOrderMsgOn(v); saveSettings(countryCode, v, serverUrl); }} style={{ width: 38, height: 20, borderRadius: 10, cursor: "pointer", position: "relative", background: orderMsgOn ? T.indigo : T.border2, flexShrink: 0, transition: "background 0.2s" }}>
                    <div style={{ position: "absolute", top: 2, left: orderMsgOn ? 19 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
                  </div>
                </div>

                {/* Country code */}
                <div>
                  <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: T.text3, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>Country Code (for phone → WhatsApp)</label>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text3, fontSize: 13 }}>+</span>
                    <input value={countryCode} onChange={e => setCountryCode(e.target.value.replace(/\D/g, ""))} onBlur={() => saveSettings(countryCode, orderMsgOn, serverUrl)} placeholder="92" maxLength={4}
                      style={{ width: 70, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text1, fontSize: 13, fontFamily: "monospace", outline: "none" }}
                      onFocus={e => (e.target.style.borderColor = T.indigo)} />
                    <span style={{ fontSize: 12, color: T.text3 }}>
                      {countryCode === "92" ? "🇵🇰 Pakistan" : countryCode === "1" ? "🇺🇸 USA" : countryCode === "44" ? "🇬🇧 UK" : countryCode === "91" ? "🇮🇳 India" : `+${countryCode}`}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: T.text3, marginTop: 4 }}>0300xxx → +{countryCode}300xxx on WhatsApp</div>
                </div>
              </div>

              {/* Test message */}
              <div style={{ padding: "22px 24px", borderRadius: 16, background: T.bg1, border: `1px solid ${T.border1}` }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 4 }}>Send Test Message</div>
                <div style={{ fontSize: 12, color: T.text3, marginBottom: 14, lineHeight: 1.5 }}>Verify the bot can send WhatsApp messages before a real order comes in.</div>
                <input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="Your WhatsApp number e.g. 03001234567"
                  style={{ width: "100%", padding: "9px 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text1, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 10 }}
                  onFocus={e => (e.target.style.borderColor = T.indigo)} onBlur={e => (e.target.style.borderColor = T.border2)} />
                {testErr && <div style={{ fontSize: 12, color: "#f43f5e", marginBottom: 8 }}>{testErr}</div>}
                {testMsg && <div style={{ fontSize: 12, color: "#22c55e", marginBottom: 8 }}>{testMsg}</div>}
                <Btn variant="primary" onClick={sendTest} disabled={testLoading} style={{ width: "100%" }}>
                  {testLoading ? "Sending…" : "Send Test Message"}
                </Btn>
              </div>

              {/* Auto messages list */}
              <div style={{ padding: "16px 18px", borderRadius: 12, background: T.bg1, border: `1px solid ${T.border1}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text1, marginBottom: 10 }}>Auto-messages triggered for:</div>
                {[
                  ["processing / on-hold", "New order → Order Confirmation"],
                  ["completed",            "Delivered → Thank you message"],
                  ["cancelled",            "Cancelled → Cancellation notice"],
                  ["refunded",             "Refund → Refund confirmation"],
                ].map(([s, l]) => (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    <span style={{ fontSize: 12, color: T.text2, flex: 1 }}>{l}</span>
                    <code style={{ fontSize: 10, color: T.text3, fontFamily: "monospace" }}>{s}</code>
                  </div>
                ))}
                <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 9, background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.18)", fontSize: 11, color: T.text3, lineHeight: 1.6 }}>
                  <strong style={{ color: T.indigo }}>Pipeline sync:</strong> Every confirmed order automatically moves the customer to <strong style={{ color: T.indigo }}>My Client</strong> stage with the order value.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CONNECT / SETTINGS TAB */}
        {tab === "connect" && (
          <div style={{ padding: "24px 28px", maxWidth: 540 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text1, marginBottom: 4 }}>
              {status?.connected ? "Update Credentials" : "Connect Your WooCommerce Store"}
            </div>
            <div style={{ fontSize: 12, color: T.text3, marginBottom: 20, lineHeight: 1.6 }}>
              Enter your WordPress site URL and WooCommerce REST API keys with <strong>Read/Write</strong> permission (needed for webhook auto-registration).
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>WordPress Site URL</label>
              <input value={siteUrl} onChange={e => setSiteUrl(e.target.value)} placeholder="https://yourstore.com"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text1, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                onFocus={e => (e.target.style.borderColor = T.indigo)} onBlur={e => (e.target.style.borderColor = T.border2)} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Consumer Key</label>
              <input value={ck} onChange={e => setCk(e.target.value)} placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text1, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                onFocus={e => (e.target.style.borderColor = T.indigo)} onBlur={e => (e.target.style.borderColor = T.border2)} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Consumer Secret</label>
              <div style={{ position: "relative" }}>
                <input type={showCs ? "text" : "password"} value={cs} onChange={e => setCs(e.target.value)} placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  style={{ width: "100%", padding: "10px 38px 10px 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg2, color: T.text1, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
                  onFocus={e => (e.target.style.borderColor = T.indigo)} onBlur={e => (e.target.style.borderColor = T.border2)} />
                <button onClick={() => setShowCs(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: T.text3, padding: 2 }}>
                  {showCs ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>}
                </button>
              </div>
            </div>

            {error   && <div style={{ padding: "10px 14px", borderRadius: 9, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: "#f43f5e", fontSize: 13, marginBottom: 14 }}>{error}</div>}
            {success && <div style={{ padding: "10px 14px", borderRadius: 9, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#22c55e", fontSize: 13, marginBottom: 14 }}>{success}</div>}

            <Btn variant="primary" onClick={connect} disabled={saving} style={{ width: "100%" }}>
              {saving ? "Connecting…" : status?.connected ? "Update Connection" : "Connect WooCommerce"}
            </Btn>

            <details style={{ marginTop: 18 }}>
              <summary style={{ fontSize: 12, color: T.indigo, cursor: "pointer", fontWeight: 600 }}>How to get API keys?</summary>
              <div style={{ marginTop: 10, fontSize: 12, color: T.text3, lineHeight: 1.8, borderLeft: `3px solid ${T.border2}`, paddingLeft: 12 }}>
                <div>1. WordPress Admin → <strong style={{ color: T.text2 }}>WooCommerce → Settings → Advanced → REST API</strong></div>
                <div>2. Click <strong style={{ color: T.text2 }}>Add key</strong> → set permissions to <code style={{ color: T.indigo }}>Read/Write</code></div>
                <div>3. Click <strong style={{ color: T.text2 }}>Generate API key</strong></div>
                <div>4. Copy <code style={{ color: T.indigo }}>Consumer key</code> and <code style={{ color: T.indigo }}>Consumer secret</code></div>
                <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 7, background: T.bg3, color: "#f59e0b" }}>⚠️ Save the Consumer Secret immediately — it won't be shown again.</div>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
